import React, { useState, useMemo } from 'react';
import './TreeDataExplorer.css';

const TreeDataExplorer = ({ 
  data = [], 
  columns = [], 
  title = "Tree Data Explorer",
  onDataChange = null 
}) => {
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [showLeafData, setShowLeafData] = useState(true);
  const [aggregateType, setAggregateType] = useState('count'); // count, sum, avg
  const [aggregateColumn, setAggregateColumn] = useState('');
  const [columnSelectionCollapsed, setColumnSelectionCollapsed] = useState(false);
  const [selectedLeafColumns, setSelectedLeafColumns] = useState([]);

  // Get available columns for hierarchy selection
  const availableColumns = useMemo(() => {
    return columns.filter(col => 
      !selectedColumns.includes(col.name) && 
      col.type !== 'textarea' // Exclude large text fields
    );
  }, [columns, selectedColumns]);

  // Calculate aggregate values for nodes
  const calculateAggregate = (items) => {
    if (!aggregateColumn || aggregateType === 'count') {
      return items.length;
    }

    const values = items
      .map(item => parseFloat(item[aggregateColumn]))
      .filter(val => !isNaN(val));

    if (values.length === 0) return 0;

    switch (aggregateType) {
      case 'sum':
        return values.reduce((sum, val) => sum + val, 0);
      case 'avg':
        return values.reduce((sum, val) => sum + val, 0) / values.length;
      default:
        return values.length;
    }
  };

  // Build tree data structure
  const treeData = useMemo(() => {
    if (selectedColumns.length === 0 || data.length === 0) return [];

    const buildTree = (items, level = 0) => {
      if (level >= selectedColumns.length) return items;

      const currentColumn = selectedColumns[level];
      const grouped = {};

      items.forEach(item => {
        const value = item[currentColumn] || 'N/A';
        if (!grouped[value]) {
          grouped[value] = [];
        }
        grouped[value].push(item);
      });

      return Object.entries(grouped).map(([value, childItems]) => ({
        key: `${selectedColumns.slice(0, level + 1).join('.')}.${value}`,
        value,
        column: currentColumn,
        level,
        count: childItems.length,
        children: level === selectedColumns.length - 1 ? childItems : buildTree(childItems, level + 1),
        isLeaf: level === selectedColumns.length - 1,
        aggregate: calculateAggregate(childItems)
      }));
    };

    return buildTree(data);
  }, [data, selectedColumns, aggregateType, aggregateColumn, calculateAggregate]);

  // Filter tree data based on search term
  const filteredTreeData = useMemo(() => {
    if (!searchTerm) return treeData;

    const filterNode = (node) => {
      const matchesSearch = node.value.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (node.isLeaf) {
        if (matchesSearch) return node;
        
        // Filter leaf data
        const filteredChildren = node.children.filter(item =>
          Object.values(item).some(value =>
            String(value).toLowerCase().includes(searchTerm.toLowerCase())
          )
        );
        
        if (filteredChildren.length > 0) {
          return {
            ...node,
            children: filteredChildren,
            count: filteredChildren.length
          };
        }
        return null;
      }

      const filteredChildren = node.children
        .map(filterNode)
        .filter(Boolean);

      if (matchesSearch || filteredChildren.length > 0) {
        return {
          ...node,
          children: filteredChildren,
          count: filteredChildren.reduce((sum, child) => sum + child.count, 0)
        };
      }

      return null;
    };

    return treeData.map(filterNode).filter(Boolean);
  }, [treeData, searchTerm]);

  // Handle column selection
  const handleColumnSelect = (columnName) => {
    setSelectedColumns(prev => {
      const newColumns = [...prev, columnName];
      // Auto-collapse after first column is selected
      if (newColumns.length === 1) {
        setColumnSelectionCollapsed(true);
      }
      return newColumns;
    });
    setExpandedNodes(new Set()); // Reset expanded nodes when hierarchy changes
  };

  const handleColumnRemove = (index) => {
    setSelectedColumns(prev => {
      const newColumns = prev.filter((_, i) => i !== index);
      // Expand column selection if no columns left
      if (newColumns.length === 0) {
        setColumnSelectionCollapsed(false);
      }
      return newColumns;
    });
    setExpandedNodes(new Set());
  };

  // Handle leaf column selection
  const handleLeafColumnToggle = (columnName) => {
    setSelectedLeafColumns(prev => {
      if (prev.includes(columnName)) {
        return prev.filter(col => col !== columnName);
      } else {
        return [...prev, columnName];
      }
    });
  };

  // Initialize leaf columns with first few important columns
  React.useEffect(() => {
    if (columns.length > 0 && selectedLeafColumns.length === 0) {
      // Select first 5 columns or all if less than 5
      const defaultColumns = columns.slice(0, Math.min(5, columns.length)).map(col => col.name);
      setSelectedLeafColumns(defaultColumns);
    }
  }, [columns, selectedLeafColumns.length]);

  // Handle node expansion
  const toggleNode = (nodeKey) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeKey)) {
      newExpanded.delete(nodeKey);
    } else {
      newExpanded.add(nodeKey);
    }
    setExpandedNodes(newExpanded);
  };

  const expandAll = () => {
    const getAllKeys = (nodes) => {
      const keys = [];
      nodes.forEach(node => {
        keys.push(node.key);
        if (node.children && !node.isLeaf) {
          keys.push(...getAllKeys(node.children));
        }
      });
      return keys;
    };
    setExpandedNodes(new Set(getAllKeys(filteredTreeData)));
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  // Get numeric columns for aggregation
  const numericColumns = useMemo(() => {
    return columns.filter(col => 
      ['int', 'float', 'decimal2', 'decimal3', 'decimal4', 'currency', 'comma'].includes(col.type)
    );
  }, [columns]);

  // Render tree node
  const renderTreeNode = (node, depth = 0) => {
    const isExpanded = expandedNodes.has(node.key);
    const hasChildren = node.children && node.children.length > 0;
    const indentStyle = { paddingLeft: `${depth * 20 + 16}px` };

    return (
      <div key={node.key} className="tree-node">
        <div 
          className={`tree-node-header ${hasChildren ? 'expandable' : ''} ${isExpanded ? 'expanded' : ''}`}
          style={indentStyle}
          onClick={() => hasChildren && toggleNode(node.key)}
        >
          <div className="tree-node-content">
            {hasChildren && (
              <span className="expand-icon">
                {isExpanded ? '▼' : '▶'}
              </span>
            )}
            <span className="node-value">{node.value}</span>
            <span className="node-count">
              ({node.count} record{node.count !== 1 ? 's' : ''})
            </span>
            {aggregateType !== 'count' && aggregateColumn && (
              <span className="node-aggregate">
                {aggregateType}: {typeof node.aggregate === 'number' ? node.aggregate.toFixed(2) : node.aggregate}
              </span>
            )}
          </div>
          <span className="node-level-label">{node.column}</span>
        </div>

        {isExpanded && hasChildren && (
          <div className="tree-node-children">
            {node.isLeaf && showLeafData ? (
              <div className="leaf-data-container" style={{ paddingLeft: `${(depth + 1) * 20 + 16}px` }}>
                <div className="leaf-data-header">
                  <h4>Records ({node.children.length})</h4>
                </div>
                <div className="leaf-data-table">
                  <table>
                    <thead>
                      <tr>
                        {columns
                          .filter(col => selectedLeafColumns.includes(col.name))
                          .map(col => (
                            <th key={col.name}>{col.label || col.name}</th>
                          ))}
                      </tr>
                    </thead>
                    <tbody>
                      {node.children.map((record, index) => (
                        <tr key={index}>
                          {columns
                            .filter(col => selectedLeafColumns.includes(col.name))
                            .map(col => (
                              <td key={col.name}>
                                {record[col.name] || '-'}
                              </td>
                            ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              node.children.map(childNode => renderTreeNode(childNode, depth + 1))
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="tree-data-explorer">
      <div className="tree-explorer-header">
        <h3>{title}</h3>
        <div className="tree-stats">
          {data.length} total records • {selectedColumns.length} hierarchy levels
        </div>
      </div>

      {/* Column Selection */}
      <div className={`column-selection-panel ${columnSelectionCollapsed ? 'collapsed' : ''}`}>
        <div className="panel-header">
          <div className="panel-title-section">
            <h4>📊 Build Hierarchy</h4>
            <button 
              className="collapse-toggle"
              onClick={() => setColumnSelectionCollapsed(!columnSelectionCollapsed)}
              title={columnSelectionCollapsed ? "Expand" : "Collapse"}
            >
              {columnSelectionCollapsed ? '📋' : '📝'}
            </button>
          </div>
          {!columnSelectionCollapsed && (
            <div className="panel-help">
              Select columns to create hierarchy levels. First column = top level.
            </div>
          )}
        </div>

        {/* Selected Columns - Always visible */}
        {selectedColumns.length > 0 && (
          <div className="selected-columns">
            <div className="hierarchy-list-compact">
              {selectedColumns.map((colName, index) => {
                const column = columns.find(col => col.name === colName);
                return (
                  <div key={colName} className="hierarchy-item-compact">
                    <span className="hierarchy-level">{index + 1}</span>
                    <span className="hierarchy-column">
                      {column?.label || colName}
                    </span>
                    <button 
                      className="remove-column-btn"
                      onClick={() => handleColumnRemove(index)}
                      title="Remove from hierarchy"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Available Columns - Only when expanded */}
        {!columnSelectionCollapsed && availableColumns.length > 0 && (
          <div className="available-columns">
            <h5>Add Level:</h5>
            <div className="column-buttons">
              {availableColumns.map(column => (
                <button
                  key={column.name}
                  className="column-select-btn"
                  onClick={() => handleColumnSelect(column.name)}
                  title={column.desc || `Add ${column.label || column.name} to hierarchy`}
                >
                  + {column.label || column.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      {selectedColumns.length > 0 && (
        <div className="tree-controls-panel">
          <div className="tree-controls">
            <div className="control-group">
              <label>🔍 Search:</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search in tree..."
                className="search-input"
              />
            </div>

            <div className="control-group">
              <button onClick={expandAll} className="control-btn">
                📂 Expand All
              </button>
              <button onClick={collapseAll} className="control-btn">
                📁 Collapse All
              </button>
            </div>

            {numericColumns.length > 0 && (
              <div className="control-group">
                <label>📈 Aggregate:</label>
                <select 
                  value={aggregateType} 
                  onChange={(e) => setAggregateType(e.target.value)}
                  className="aggregate-select"
                >
                  <option value="count">Count</option>
                  <option value="sum">Sum</option>
                  <option value="avg">Average</option>
                </select>
                
                {aggregateType !== 'count' && (
                  <select
                    value={aggregateColumn}
                    onChange={(e) => setAggregateColumn(e.target.value)}
                    className="aggregate-column-select"
                  >
                    <option value="">Select Column</option>
                    {numericColumns.map(col => (
                      <option key={col.name} value={col.name}>
                        {col.label || col.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            <div className="control-group">
              <label>
                <input
                  type="checkbox"
                  checked={showLeafData}
                  onChange={(e) => setShowLeafData(e.target.checked)}
                />
                Show Record Details
              </label>
            </div>

            {showLeafData && (
              <div className="control-group leaf-columns-control">
                <label>📋 Show Columns:</label>
                <div className="leaf-column-selector">
                  {columns.map(col => (
                    <label key={col.name} className="leaf-column-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedLeafColumns.includes(col.name)}
                        onChange={() => handleLeafColumnToggle(col.name)}
                      />
                      <span className="checkbox-label">{col.label || col.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tree View */}
      <div className="tree-view-panel">
        {selectedColumns.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🌳</div>
            <h3>Build Your Data Tree</h3>
            <p>Select columns above to create a hierarchical view of your data.</p>
            <p>Each column becomes a level in the tree structure.</p>
          </div>
        ) : filteredTreeData.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <h3>No Results Found</h3>
            <p>No data matches your current search criteria.</p>
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="clear-search-btn"
              >
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <div className="tree-container">
            <div className="tree-summary">
              <strong>{filteredTreeData.length}</strong> top-level group{filteredTreeData.length !== 1 ? 's' : ''}
              {searchTerm && <span> (filtered)</span>}
            </div>
            <div className="tree-nodes">
              {filteredTreeData.map(node => renderTreeNode(node))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TreeDataExplorer; 